import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { INVITE_CHANNELS } from '@/api/types';
import { toast } from 'sonner';
import { z } from 'zod';
import { AlertTriangle, Check, Loader2, Send } from 'lucide-react';
import {
  INVALID_HANDLE,
  POSITIVE_REPLY,
  SCREENSHOT_RULE,
  STEPS,
  channelLabel,
  duplicateMessage,
  normalizeHandle,
} from '@/lib/outreach-copy';
import { creatorTypeLabel, inviteStatusLabel } from '@/lib/labels';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { useRecordContact, useWithdrawContact } from '@/hooks/useContacts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import type {
  CreatorRequest,
  InviteChannel,
  OutreachInvite,
  OutreachNiche,
  OutreachRate,
} from '@/api/types';
import { useProspectSearch, type ProspectMatch } from '@/hooks/useProspectSearch';
import { useClippingMembership } from '@/hooks/useClippingMembership';
import { useDebounce } from '@/hooks/useDebounce';
import { Pill } from '@/components/ui/pill';
import { Combobox } from '@/components/ui/combobox';

// Channel is validated against the inviter's team allowed-channels at
// submit time (see onSubmit) — the schema only enforces presence so the
// dropdown can host whatever the team's `allowedChannels` returns.
const FormSchema = z.object({
  channel: z.string().min(1, 'Pick a channel'),
  category: z.string().min(1, 'Pick a category'),
  targetIdentifier: z.string().min(1, 'Required'),
  niches: z.array(z.string()).default([]),
  email: z.string().email().optional().or(z.literal('')),
  notes: z.string().max(2000).optional(),
  /** Optional creator/niche request publicId to attach this invite to. */
  attachToRequestPublicId: z.string().optional().default(''),
});

type FormValues = z.infer<typeof FormSchema>;

const HANDLE_PLACEHOLDER: Record<string, string> = {
  TIKTOK: '@example  ·  example  ·  https://www.tiktok.com/@example',
  INSTAGRAM: '@example  ·  example  ·  https://instagram.com/example',
  YOUTUBE: '@example  ·  example  ·  https://youtube.com/@example',
  TWITTER: '@example  ·  example  ·  https://x.com/example',
  DISCORD: 'their Discord username',
};

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function LogInvitePage(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const me = useMe();

  const screenshotRequired =
    me.data?.role === 'outreacher' ? me.data.teamSettings.screenshotRequired : false;
  const multipleNichesAllowed =
    me.data?.role === 'outreacher' ? me.data.teamSettings.multipleNichesAllowed : false;
  const allowedCategories =
    me.data?.role === 'outreacher' ? me.data.team?.allowedCategories ?? [] : [];
  const allowedChannels: InviteChannel[] =
    me.data?.role === 'outreacher' ? me.data.team?.allowedChannels ?? [] : [];
  const myInviterId = me.data?.role === 'outreacher' ? me.data.inviter.id : null;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      channel: '',
      category: '',
      targetIdentifier: '',
      niches: [],
      email: '',
      notes: '',
      attachToRequestPublicId: '',
    },
  });
  const selectedChannel = form.watch('channel');
  const selectedCategory = form.watch('category');
  const selectedNiches = form.watch('niches');
  const rawHandle = form.watch('targetIdentifier');

  // Duplicate check, lifted here so a duplicate can block submit. Debounced
  // so we don't search on every keystroke; works for every channel the team
  // allows, using the same normalisation the backend applies on save.
  const debouncedHandle = useDebounce(rawHandle ?? '', 350);
  const debouncedParsed = normalizeHandle(selectedChannel, debouncedHandle);
  const liveParsed = normalizeHandle(selectedChannel, rawHandle);
  const duplicateQuery = useProspectSearch({
    q: debouncedParsed?.username ?? '',
    ...(selectedChannel ? { channel: selectedChannel as InviteChannel } : {}),
    exact: true,
    enabled: Boolean(debouncedParsed?.username && selectedChannel),
  });
  // Only trust the result while it still describes what's in the box.
  const duplicate: ProspectMatch | null =
    liveParsed && debouncedParsed && liveParsed.username === debouncedParsed.username
      ? (duplicateQuery.data?.[0] ?? null)
      : null;

  // "Log reply" from My creators' Messaged tab opens this page with the
  // creator filled in (?channel=TIKTOK&handle=name). Applied once, on arrival.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ch = searchParams.get('channel');
    const handle = searchParams.get('handle');
    if (ch && (INVITE_CHANNELS as readonly string[]).includes(ch)) {
      form.setValue('channel', ch, { shouldDirty: false });
    }
    if (handle) form.setValue('targetIdentifier', handle, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once `me` resolves, populate the channel + category defaults. We hide the
  // picker entirely when the team only has one valid option (no real choice
  // to surface), and otherwise leave the field empty so the outreacher picks.
  useEffect(() => {
    if (!selectedCategory && allowedCategories.length === 1 && allowedCategories[0]) {
      form.setValue('category', allowedCategories[0], { shouldDirty: false });
    }
  }, [allowedCategories, selectedCategory, form]);
  useEffect(() => {
    if (!selectedChannel && allowedChannels.length === 1 && allowedChannels[0]) {
      form.setValue('channel', allowedChannels[0], { shouldDirty: false });
    }
  }, [allowedChannels, selectedChannel, form]);

  // Niche list reacts to category so outreachers don't pick a niche that's
  // out-of-scope. Empty-categoryCodes niches are returned globally by the API.
  const niches = useQuery({
    queryKey: ['niches', selectedCategory],
    queryFn: () =>
      api.get<OutreachNiche[]>(
        '/portal/niches',
        selectedCategory ? { category: selectedCategory } : undefined,
      ),
    enabled: Boolean(selectedCategory) || allowedCategories.length === 0,
  });

  // Rate card — we need this to know which (category, niche) pairs actually
  // have a niche bonus configured AND how much. The "BONUS" badge only
  // appears for niches with a real rate row (amount > 0) for the selected
  // category; the per-chip "+$N" indicator shows the sum of every bonus
  // row that applies to that (category, niche).
  const rates = useQuery({
    queryKey: ['portal-rates'],
    queryFn: () => api.get<OutreachRate[]>('/portal/rates'),
  });
  const bonusAmounts = new Map<string, number>();
  for (const r of rates.data ?? []) {
    if (!r.isActive) continue;
    if (r.niche === null) continue;
    if (r.category !== selectedCategory) continue;
    const amount = Number.parseFloat(r.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    bonusAmounts.set(r.niche, (bonusAmounts.get(r.niche) ?? 0) + amount);
  }

  // Alphabetical. Pick the niche that describes the creator, not the one
  // that pays most: bonuses are still marked on each chip.
  const orderedNiches = [...(niches.data ?? [])].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );

  function toggleNiche(code: string): void {
    const current = form.getValues('niches');
    const next = current.includes(code)
      ? current.filter((n) => n !== code)
      : [...current, code];
    form.setValue('niches', next, { shouldDirty: true });
  }

  function onCategoryChange(value: string): void {
    form.setValue('category', value, { shouldDirty: true, shouldValidate: true });
    // Selected niches may not apply to the new category — start fresh.
    form.setValue('niches', [], { shouldDirty: true });
  }

  // Every open request targeting this inviter's team — not just the ones
  // assigned to them. An outreacher logging an invite for a teammate's
  // request should be able to attach without that teammate having to
  // re-assign it first. The portal route is already team-scoped via
  // requirePortalClerk, so dropping `assignedInviterId` is enough.
  const teamOpenRequests = useQuery({
    queryKey: ['inviter', 'creator-requests', 'team-open'],
    queryFn: () =>
      api.getPaged<CreatorRequest[]>('/portal/creator-requests', {
        status: 'REQUESTED,IN_PROGRESS,AWAITING_RESPONSE,AWAITING_RATE',
        limit: 50,
      }),
    enabled: me.data?.role === 'outreacher',
  });

  const mutation = useMutation({
    mutationFn: (args: { values: FormValues; file: File | null }) => {
      const { values, file } = args;
      const fd = new FormData();
      fd.append('channel', values.channel);
      fd.append('category', values.category);
      fd.append('targetIdentifier', values.targetIdentifier.trim());
      fd.append('niches', JSON.stringify(values.niches));
      if (values.email) fd.append('email', values.email);
      if (values.notes) fd.append('notes', values.notes);
      if (values.attachToRequestPublicId) {
        fd.append('attachToRequestPublicId', values.attachToRequestPublicId);
      }
      if (file) fd.append('file', file);
      return api.postForm<OutreachInvite & { attachWarning?: string }>(
        '/portal/invites',
        fd,
      );
    },
    onSuccess: (invite) => {
      // Stay on the form so the outreacher can rip another invite without
      // detouring. Keep channel + category locked in (most outreachers log
      // in batches against the same target type); clear handle, niches,
      // email, notes, and the screenshot file input.
      toast.success(`Logged @${invite.targetIdentifier}. Log another?`);
      if (invite.attachWarning) {
        toast.warning(invite.attachWarning);
      }
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['inviter', 'creator-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['prospect-search'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'outreach-history'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'discovery-prospects'] });
      form.reset({
        channel: form.getValues('channel'),
        category: form.getValues('category'),
        targetIdentifier: '',
        niches: [],
        email: '',
        notes: '',
        attachToRequestPublicId: form.getValues('attachToRequestPublicId'),
      });
      const fileInput = document.getElementById('screenshot') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';
      // Focus the handle field so the next entry is one keystroke away.
      const handleInput = document.getElementById('targetIdentifier') as HTMLInputElement | null;
      handleInput?.focus();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  async function onSubmit(values: FormValues): Promise<void> {
    if (!(allowedChannels as string[]).includes(values.channel)) {
      toast.error('Pick a channel your team supports');
      return;
    }
    if (!normalizeHandle(values.channel, values.targetIdentifier)) {
      toast.error(INVALID_HANDLE);
      return;
    }
    if (duplicate) {
      toast.error(duplicateMessage(duplicate, { isMe: duplicate.inviterId === myInviterId }));
      return;
    }

    const fileInput = (document.getElementById('screenshot') as HTMLInputElement | null) ?? null;
    const file = fileInput?.files?.[0] ?? null;

    if (file) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Screenshot must be JPEG, PNG, or WebP');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Screenshot must be under 5MB');
        return;
      }
    } else if (screenshotRequired) {
      toast.error(SCREENSHOT_RULE.missingError);
      return;
    }

    mutation.mutate({ values, file });
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Log reply"
        description={
          <>
            Only log a creator who replied asking how to join. If they haven't replied yet, use{' '}
            <span className="font-medium text-foreground">“{STEPS.messaged.verb}”</span> below instead.
            That records the message without logging anything.
          </>
        }
      />

      {/* The bar for logging, stated before the form rather than beside the
          file input — this is the rule that decides whether the invite stands
          up in a screenshot audit. */}
      <div className="rounded-lg border-2 border-warning/50 bg-warning/[0.08] px-4 py-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div className="space-y-1">
            <p className="text-base font-bold text-foreground">{POSITIVE_REPLY.headline}</p>
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-foreground">Counts:</span>{' '}
              {POSITIVE_REPLY.qualifies}
            </p>
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-foreground">Doesn't count:</span>{' '}
              {POSITIVE_REPLY.doesNot}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Creator</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {allowedChannels.length > 1 ? (
              <div className="grid gap-2">
                <Label htmlFor="channel">Channel</Label>
                <Combobox
                  value={form.watch('channel')}
                  onChange={(v) => form.setValue('channel', v, { shouldValidate: true })}
                  options={allowedChannels.map((c) => ({
                    value: c,
                    label: channelLabel(c),
                  }))}
                  placeholder="Pick a channel"
                />
                {form.formState.errors.channel && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.channel.message}
                  </p>
                )}
              </div>
            ) : allowedChannels.length === 0 ? (
              <p className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-xs text-destructive">
                Your team has no channels set up yet. Ask an admin to turn one on before you log
                replies.
              </p>
            ) : null}

            {allowedCategories.length > 1 && (
              <div className="grid gap-2">
                <Label htmlFor="category">Creator type</Label>
                <Combobox
                  value={selectedCategory}
                  onChange={(v) => onCategoryChange(v)}
                  options={allowedCategories.map((c) => ({ value: c, label: creatorTypeLabel(c) }))}
                  placeholder="Pick a creator type"
                />
                <p className="text-xs text-muted-foreground">
                  Your team works with more than one creator type. Pick the one that fits this
                  creator; it decides which rates apply.
                </p>
                {form.formState.errors.category && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.category.message}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="targetIdentifier">Username, handle, or profile URL</Label>
              <Input
                id="targetIdentifier"
                autoComplete="off"
                placeholder={HANDLE_PLACEHOLDER[selectedChannel] ?? '@example  ·  example  ·  a profile link'}
                {...form.register('targetIdentifier')}
              />
              <HandlePreview channel={selectedChannel} raw={rawHandle} />
              {duplicate && (
                <DuplicateWarning match={duplicate} isMe={duplicate.inviterId === myInviterId} />
              )}
              <ClippingMemberWarning channel={selectedChannel} raw={rawHandle} />
              <MessagedAction channel={selectedChannel} raw={rawHandle} />
              {form.formState.errors.targetIdentifier && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.targetIdentifier.message}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="niche">
                {multipleNichesAllowed ? 'Niches (optional)' : 'Niche (optional)'}
              </Label>
              <p className="text-xs text-muted-foreground">
                Pick what the creator actually makes. ★ marks a focus niche.
              </p>
              {multipleNichesAllowed ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-brand">Highlighted</span> niches carry a
                    niche bonus for this creator type. Only your{' '}
                    <span className="font-medium text-foreground">first pick</span> can earn it;
                    the others are kept for context.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {orderedNiches.map((n) => {
                      const index = selectedNiches.indexOf(n.code);
                      const active = index !== -1;
                      const isFirst = index === 0;
                      const bonus = bonusAmounts.get(n.code) ?? 0;
                      const hasBonus = bonus > 0;
                      const earnsBonus = isFirst && hasBonus;
                      const bonusLabel = hasBonus
                        ? `+$${Number.isInteger(bonus) ? bonus.toFixed(0) : bonus.toFixed(2)}`
                        : null;

                      // Visual tiers:
                      //   1. selected (any) → solid primary, inverse text.
                      //      The BONUS badge does the work of differentiating
                      //      the first-selected niche; we don't dim the others.
                      //   2. unselected + has bonus → soft brand wash so the
                      //      prize-bearing options jump out before any pick.
                      //   3. unselected + no bonus → ghost outline.
                      const chipClass = active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : hasBonus
                          ? 'border-brand/50 bg-brand/10 text-foreground hover:bg-brand/20'
                          : 'border-input bg-transparent text-foreground hover:bg-muted';

                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => toggleNiche(n.code)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${chipClass}`}
                          title={
                            earnsBonus
                              ? `Earns +$${bonus.toFixed(2)} niche bonus`
                              : active
                                ? hasBonus
                                  ? `Selected (#${index + 1}). Only the first pick can earn the bonus.`
                                  : `Selected (#${index + 1})`
                                : hasBonus
                                  ? `Bonus available (+$${bonus.toFixed(2)}). Pick it first to earn it.`
                                  : 'Click to select'
                          }
                        >
                          <span>{n.displayName}</span>
                          {n.isFocused && (
                            <span
                              className={
                                active
                                  ? 'text-[10px] text-primary-foreground/90'
                                  : 'text-[10px] text-warning'
                              }
                              aria-label="Focus niche"
                            >
                              ★
                            </span>
                          )}
                          {bonusLabel && (
                            <span
                              className={
                                active
                                  ? 'rounded-sm bg-background/25 px-1 text-[9px] font-mono font-semibold tracking-wide text-primary-foreground'
                                  : 'rounded-sm bg-brand/15 px-1 text-[9px] font-mono font-semibold tracking-wide text-brand'
                              }
                            >
                              {bonusLabel}
                            </span>
                          )}
                          {earnsBonus && (
                            <span className="rounded-sm bg-background/30 px-1 text-[9px] font-semibold uppercase tracking-wider text-primary-foreground">
                              Bonus
                            </span>
                          )}
                          {active && !earnsBonus && (
                            <span className="text-[9px] font-medium text-primary-foreground/75">
                              #{index + 1}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {(niches.data ?? []).length === 0 && (
                      <p className="text-xs text-muted-foreground">No niches configured.</p>
                    )}
                  </div>
                  {selectedNiches.length > 1 && (
                    <p className="text-xs text-muted-foreground">
                      To change which niche earns the bonus, unselect your first pick and select
                      them again in the order you want.
                    </p>
                  )}
                </>
              ) : (
                <Combobox
                  value={selectedNiches[0] ?? ''}
                  onChange={(v) =>
                    form.setValue('niches', v ? [v] : [], { shouldDirty: true })
                  }
                  options={orderedNiches.map((n) => {
                    const bonus = bonusAmounts.get(n.code) ?? 0;
                    return {
                      value: n.code,
                      label: n.displayName,
                      ...(bonus > 0
                        ? {
                            badge: `+$${
                              Number.isInteger(bonus) ? bonus.toFixed(0) : bonus.toFixed(2)
                            }`,
                          }
                        : {}),
                      ...(n.isFocused ? { hint: '★' } : {}),
                    };
                  })}
                  allowAll
                  allLabel="None"
                  placeholder="Search niches…"
                />
              )}
            </div>

            {(teamOpenRequests.data?.data ?? []).length > 0 && (
              <div className="grid gap-2">
                <Label htmlFor="attachToRequestPublicId">
                  Attach to a request <span className="text-xs text-muted-foreground">(optional)</span>
                </Label>
                <Combobox
                  value={form.watch('attachToRequestPublicId') ?? ''}
                  onChange={(v) =>
                    form.setValue('attachToRequestPublicId', v, { shouldDirty: true })
                  }
                  options={(teamOpenRequests.data?.data ?? []).map((r) => ({
                    value: r.publicId,
                    label:
                      r.requestType === 'CREATOR'
                        ? `Creator: ${r.targetIdentifier}`
                        : `Niche: ${
                            r.niches.length > 0
                              ? r.niches
                                  .map((code) => niches.data?.find((n) => n.code === code)?.displayName ?? code)
                                  .join(' + ')
                              : 'any'
                          }`,
                    hint: r.requesterName ? `from ${r.requesterName}` : r.publicId,
                  }))}
                  allowAll
                  allLabel="None"
                  placeholder="Pick one of your team's open requests"
                />
                <p className="text-xs text-muted-foreground">
                  Counts this invite toward a campaign manager's request, so they can see your
                  team's progress without anyone adding it later.
                </p>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="email">Contact email (optional)</Label>
              <Input id="email" type="email" {...form.register('email')} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <textarea
                id="notes"
                rows={3}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                {...form.register('notes')}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="screenshot">
                Screenshot of their reply
                <span
                  className={`ml-2 text-xs ${
                    screenshotRequired ? 'font-medium text-destructive' : 'text-muted-foreground'
                  }`}
                >
                  {SCREENSHOT_RULE.labelSuffix(screenshotRequired)}
                </span>
              </Label>
              <Input
                id="screenshot"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                required={screenshotRequired}
              />
              <p className="text-xs font-medium text-warning">{SCREENSHOT_RULE.short}</p>
              <p className="text-xs text-muted-foreground">{SCREENSHOT_RULE.formats}</p>
            </div>

            <div className="space-y-1.5">
              <Button type="submit" disabled={mutation.isPending || Boolean(duplicate)}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Log reply
              </Button>
              <p className="text-xs text-muted-foreground">
                {duplicate
                  ? 'This creator is already logged, so this reply can’t be logged again.'
                  : 'Creates your invite for this creator. Only for creators who replied asking how to join.'}
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export type { InviteChannel };

/**
 * Inline preview under the handle input: what the backend will store, using
 * the same parser. Every channel gets one, Discord included.
 */
function HandlePreview({ channel, raw }: { channel: string; raw: string | undefined }): JSX.Element | null {
  const value = (raw ?? '').trim();
  if (!value || !channel) return null;

  const parsed = normalizeHandle(channel, value);
  if (!parsed) {
    return <p className="text-xs text-destructive">{INVALID_HANDLE}</p>;
  }

  return (
    <p className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>Saved as</span>
      <span className="rounded-sm bg-brand/15 px-1.5 py-0.5 font-mono text-brand">{parsed.displayHandle}</span>
      {parsed.fromUrl && <span className="text-[11px] text-muted-foreground">(read from the link)</span>}
    </p>
  );
}

/**
 * Someone already holds the invite for this creator. Only one invite per
 * creator is paid, so the form refuses to submit while this shows.
 * Expired and hidden invites don't count (the search excludes them).
 */
function DuplicateWarning({ match, isMe }: { match: ProspectMatch; isMe: boolean }): JSX.Element {
  return (
    <div role="alert" className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
      <div className="flex items-start gap-2">
        <Pill tone="pending">Already logged</Pill>
        <div className="flex-1 space-y-1">
          <p className="text-foreground">{duplicateMessage(match, { isMe })}</p>
          <p className="text-muted-foreground">
            Stage: {inviteStatusLabel(match.status)}.{' '}
            {isMe
              ? 'Find it in My creators.'
              : 'Your own message to them stays on record if you tap “I messaged them”.'}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The Messaged stage for a creator the outreacher found themselves: records
 * that they sent a DM. Not a log; nothing is owed.
 */
function MessagedAction({ channel, raw }: { channel: string; raw: string | undefined }): JSX.Element {
  const record = useRecordContact();
  const withdraw = useWithdrawContact();
  // The handle this session recorded, so the button can offer Undo.
  const [recorded, setRecorded] = useState<{ channel: string; username: string } | null>(null);
  const parsed = normalizeHandle(channel, raw);
  // Always on screen (the page intro points at it), but only usable once
  // there's a handle to record.
  if (!parsed || !channel) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs">
        <span className="text-muted-foreground">No reply yet?</span>
        <Button type="button" size="sm" variant="outline" disabled title="Enter their handle first">
          <Send className="h-3.5 w-3.5" />
          {STEPS.messaged.verb}
        </Button>
        <span className="text-muted-foreground">Enter their handle above first. Nothing is owed for it.</span>
      </div>
    );
  }
  const isRecorded = recorded?.channel === channel && recorded.username === parsed.username;
  const busy = record.isPending || withdraw.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs">
      <span className="text-muted-foreground">No reply yet?</span>
      {isRecorded ? (
        <>
          <span className="inline-flex items-center gap-1 font-medium text-foreground">
            <Check className="h-3.5 w-3.5" /> {STEPS.messaged.done}
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              withdraw.mutate(
                { channel: channel as InviteChannel, targetIdentifier: parsed.username },
                {
                  onSuccess: () => {
                    setRecorded(null);
                    toast.success(`Removed your messaged mark on ${parsed.displayHandle}`);
                  },
                  onError: (err: Error) => toast.error(`Couldn't undo: ${err.message}`),
                },
              )
            }
          >
            {withdraw.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {STEPS.messaged.undo}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          title={STEPS.messaged.means}
          onClick={() =>
            record.mutate(
              { channel: channel as InviteChannel, targetIdentifier: parsed.username },
              {
                onSuccess: () => {
                  setRecorded({ channel, username: parsed.username });
                  toast.success(`Marked ${parsed.displayHandle} as messaged. Nothing is logged yet.`);
                },
                onError: (err: Error) => toast.error(`Couldn't mark as messaged: ${err.message}`),
              },
            )
          }
        >
          {record.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {STEPS.messaged.verb}
        </Button>
      )}
      <span className="text-muted-foreground">Records the DM so your team can see. Nothing is owed for it.</span>
    </div>
  );
}

/**
 * Inline clipping-platform membership check. Shows when a creator is already
 * on the clipping platform. The backend also refuses the invite; this saves
 * filling in the whole form first.
 */
function ClippingMemberWarning({ channel, raw }: { channel: string; raw: string | undefined }): JSX.Element | null {
  const debouncedRaw = useDebounce(raw ?? '', 350);
  const parsed = normalizeHandle(channel, debouncedRaw);
  const query = useClippingMembership({
    channel,
    username: parsed?.username ?? '',
    enabled: Boolean(parsed?.username),
  });
  if (!parsed) return null;
  if (query.isLoading || !query.data || !query.data.isMember) return null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs">
      <div className="flex items-start gap-2">
        <Pill tone="rejected">On platform</Pill>
        <div className="flex-1">
          <p className="text-foreground">
            <span className="font-mono font-medium">{parsed.displayHandle}</span> is already on the clipping
            platform, so there's no invite to log.
          </p>
        </div>
      </div>
    </div>
  );
}
